import { Geotab } from "@fairfleet/geotab";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useGeotabClient } from "@/components/GeotabClientContext";
import { MaintenanceWorkOrderJob, STATUS_MAP, StatusCode } from "@/models/MaintenanceWorkOrderJob";

type SetStatusArgs = {
  job: Readonly<MaintenanceWorkOrderJob>;
  newStatus: StatusCode;
};

function withStatus(
  job: Readonly<MaintenanceWorkOrderJob>,
  newStatus: StatusCode,
): MaintenanceWorkOrderJob {
  return { ...job, statusCode: newStatus };
}

function isStatusCode(value: unknown): value is StatusCode {
  return typeof value === "number" && value in STATUS_MAP;
}

async function setWorkOrderJobStatus(geotabClient: Geotab, { job, newStatus }: SetStatusArgs) {
  if (!isStatusCode(newStatus)) {
    console.error("nextStatusCode must be a statusCode: ", newStatus);
    return {};
  }

  if (job.statusCode === newStatus) return { skipped: true };

  const updated = withStatus(job, newStatus);

  return geotabClient.call("Set", {
    typeName: "MaintenanceWorkOrderJob",
    entity: updated,
  });
}

export function useUpdateMaintenanceJob() {
  const queryClient = useQueryClient();
  const { geotabClient } = useGeotabClient();

  return useMutation({
    mutationFn: async (args: { job: Readonly<MaintenanceWorkOrderJob>; newStatus: StatusCode }) =>
      await setWorkOrderJobStatus(geotabClient, args),
    // invalidate by key prefix: the live query key is
    // ["maintenanceWorkOrderJob", activeSessionId, groups, groupFilterCondition] — a 2-element
    // setQueryData would miss it and leave the diagnostic monitor detail table stale until reload.
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["maintenanceWorkOrderJob"] });
      queryClient.invalidateQueries({ queryKey: ["maintenanceWorkOrder"] });
      queryClient.invalidateQueries({ queryKey: ["pmJobsByDevice"] });
    },
  });
}
